import React, { useEffect, useContext, useState } from 'react';
import { useFormik } from "formik";
import * as Yup from "yup";
import { IoClose } from 'react-icons/io5';
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';

import { updateAdicionaisGroup, getItens, vincularItensAoGrupo } from "../../services/api";
import { AuthContext } from "../../contexts/auth";
import { formatPriceWithSymbol } from '../../utils/currency';
import '../../styles/modal-shared.css';

/**
 * ModalEditGrupoAdicionais - Componente compartilhado para editar grupos de adicionais/sabores
 */
const ModalEditGrupoAdicionais = ({
  isOpen,
  onClose,
  onSuccess,
  grupo
}) => {
  const { t } = useTranslation('categorias');
  const { user } = useContext(AuthContext);
  const empresa = localStorage.getItem('empresa');
  const empresaParse = JSON.parse(empresa);
  const idEmpresa = empresaParse.id_empresa;

  const [minimo, setMinimo] = useState(grupo?.min ?? 0);
  const [maximo, setMaximo] = useState(grupo?.max ?? 0);
  const [mandatory, setMandatory] = useState(grupo?.mandatory ?? false);
  const [precificacao, setPrecificacao] = useState(grupo?.calcular_maior_valor ? "maior" : grupo?.calcular_media ? "media" : "soma");
  const [type, setType] = useState(grupo?.type === "pizza" ? "pizza" : "simples");

  // Estados para vincular itens
  const [allItens, setAllItens] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [searchItem, setSearchItem] = useState('');
  const [loadingItens, setLoadingItens] = useState(false);
  const [savingVinculos, setSavingVinculos] = useState(false);

  useEffect(() => {
    if (grupo) {
      setMinimo(grupo.min ?? 0);
      setMaximo(grupo.max ?? 0);
      setMandatory(grupo.mandatory ?? false);
      setPrecificacao(grupo?.calcular_maior_valor ? "maior" : grupo?.calcular_media ? "media" : "soma");
      setType(grupo?.type === "pizza" ? "pizza" : "simples");
    }
  }, [isOpen, grupo]);

  // Carregar itens ao abrir o modal
  useEffect(() => {
    if (isOpen && grupo?._id) {
      setLoadingItens(true);
      setSearchItem('');
      getItens().then(response => {
        const itens = response.data?.itens || [];
        setAllItens(itens);
        // Pré-selecionar itens que já têm este grupo vinculado
        const vinculados = new Set();
        itens.forEach(item => {
          if (item.adicionais?.some(a => a.value === grupo._id)) {
            vinculados.add(item._id);
          }
        });
        setSelectedItemIds(vinculados);
      }).catch(err => {
        console.error('Erro ao buscar itens:', err);
      }).finally(() => setLoadingItens(false));
    }
  }, [isOpen, grupo?._id]);

  const toggleItem = (itemId) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  // Filtrar itens por tipo do grupo e busca
  const filteredItens = allItens.filter(item => {
    // Grupos pizza: só itens tipo pizza. Grupos simples: todos os itens
    if (type === 'pizza' && item.type !== 'pizza') return false;
    if (searchItem) {
      const termo = searchItem.toLowerCase();
      return item.title?.toLowerCase().includes(termo) ||
             item.category_item_title?.toLowerCase().includes(termo);
    }
    return true;
  });

  // Agrupar por categoria
  const itensPorCategoria = filteredItens.reduce((acc, item) => {
    const cat = item.category_item_title || t('editGroupModal.noCategory');
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const handleMinimoChange = (event) => {
    const value = Math.min(Math.max(0, event.target.valueAsNumber), maximo);
    handleMinimoChangeWithMandatory(value);
  };

  const handleMaximoChange = (event) => {
    const value = Math.max(minimo, event.target.valueAsNumber);
    setMaximo(value);
  };

  const incrementMinimo = () => handleMinimoChangeWithMandatory(minimo < maximo ? minimo + 1 : minimo);
  const decrementMinimo = () => handleMinimoChangeWithMandatory(minimo > 0 ? minimo - 1 : minimo);
  const incrementMaximo = () => setMaximo(prev => prev + 1);
  const decrementMaximo = () => setMaximo(prev => (prev > minimo ? prev - 1 : prev));

  const handleMandatoryChange = (newMandatory) => {
    if (newMandatory) {
      setMandatory(true);
      if (minimo === 0) setMinimo(1);
    } else {
      setMandatory(false);
      setMinimo(0);
    }
  };

  const handleMinimoChangeWithMandatory = (newMinimo) => {
    setMinimo(newMinimo);
    setMandatory(newMinimo > 0);
  };

  const SignUpSchema = Yup.object().shape({
    title: Yup.string().required(t('actions.required')),
  });

  const formik = useFormik({
    validationSchema: SignUpSchema,
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
    initialValues: { title: grupo?.title || "" },
    onSubmit: (values) => handleSubmitAPI(values.title),
  });

  const handleSubmitAPI = async (title) => {
    const userID = user._id;
    const adicional_objId = grupo?._id;
    const calcularMaiorValor = precificacao === "maior";
    const calcularMedia = precificacao === "media";
    const categoriaData = {
      userID, adicional_objId, idEmpresa, title,
      minimo, maximo, mandatory,
      calcularMaiorValor, calcularMedia, precificacao, type
    };

    setSavingVinculos(true);
    try {
      // 1. Salvar dados do grupo
      const response = await updateAdicionaisGroup(categoriaData);
      if (response.status !== 200) {
        toast(response.data.msg, { autoClose: 5000, type: "error" });
        return;
      }

      // 2. Salvar vínculos de itens
      await vincularItensAoGrupo(adicional_objId, Array.from(selectedItemIds));

      toast(response.data.msg, { autoClose: 5000, type: "success" });
      if (onSuccess) onSuccess(categoriaData);
      onClose();
    } catch (err) {
      toast(err.response?.data?.msg || t('cardapio:groupUpdateError'), { autoClose: 5000, type: "error" });
    } finally {
      setSavingVinculos(false);
    }
  };

  return (
    <div className={isOpen ? 'pdj-modal-overlay' : 'pdj-modal-overlay--hidden'}>
      <div className="pdj-modal-box pdj-modal-box--wide">
        <form onSubmit={formik.handleSubmit}>

          {/* Header */}
          <div className="pdj-modal-header">
            <div className="pdj-modal-header-text">
              <h2 className="pdj-modal-title">{t('editGroupModal.title')}</h2>
              <p className="pdj-modal-subtitle">{t('editGroupModal.subtitle')}</p>
            </div>
            <button type="button" className="pdj-modal-close-btn" onClick={onClose}>
              <IoClose />
            </button>
          </div>

          {/* Body */}
          <div className="pdj-modal-body">

            {/* Tipo + Nome */}
            <div className="pdj-modal-grid-1-2">
              <div className="pdj-modal-field">
                <label className="pdj-modal-label">
                  {t('editGroupModal.groupType')}
                  <span className="pdj-modal-required">*</span>
                </label>
                <select
                  className="pdj-modal-select"
                  id="type"
                  name="type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  <option value="simples">{t('editGroupModal.simpleAdditional')}</option>
                  <option value="pizza">{t('editGroupModal.pizzaFlavor')}</option>
                </select>
              </div>

              <div className="pdj-modal-field">
                <label className="pdj-modal-label">
                  {t('editGroupModal.groupName')}
                  <span className="pdj-modal-required">*</span>
                </label>
                <input
                  className="pdj-modal-input"
                  placeholder={t('editGroupModal.groupNamePlaceholder')}
                  id="title"
                  type="text"
                  name="title"
                  onChange={formik.handleChange}
                  value={formik.values.title}
                />
                {formik.errors.title && (
                  <span className="pdj-modal-error">{formik.errors.title}</span>
                )}
              </div>
            </div>

            <hr className="pdj-modal-divider" />

            {/* Quantidade + Selecao + Precificacao */}
            <div className="pdj-modal-grid-3">

              {/* Controles de Quantidade */}
              <div className="pdj-modal-field">
                <label className="pdj-modal-label">{t('editGroupModal.quantity')}</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* Minimo */}
                  <div>
                    <label className="pdj-modal-label" style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                      {t('editGroupModal.minimum')}
                    </label>
                    <div className="pdj-modal-stepper">
                      <button type="button" onClick={decrementMinimo}>-</button>
                      <input type="number" value={minimo} onChange={handleMinimoChange} />
                      <button type="button" onClick={incrementMinimo}>+</button>
                    </div>
                  </div>
                  {/* Maximo */}
                  <div>
                    <label className="pdj-modal-label" style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                      {t('editGroupModal.maximum')}
                    </label>
                    <div className="pdj-modal-stepper">
                      <button type="button" onClick={decrementMaximo}>-</button>
                      <input type="number" value={maximo} onChange={handleMaximoChange} />
                      <button type="button" onClick={incrementMaximo}>+</button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Obrigatorio / Opcional */}
              <div className="pdj-modal-field">
                <label className="pdj-modal-label">{t('editGroupModal.selectionType')}</label>
                <div className="pdj-modal-radio-group">
                  {/* Opcional */}
                  <div
                    className={`pdj-modal-radio-card${!mandatory ? ' pdj-modal-radio-card--active' : ''}`}
                    onClick={() => handleMandatoryChange(false)}
                  >
                    <input
                      type="radio"
                      name="mandatory"
                      checked={!mandatory}
                      onChange={() => handleMandatoryChange(false)}
                    />
                    <div>
                      <div className="pdj-modal-radio-card-label">{t('editGroupModal.optional')}</div>
                      <div className="pdj-modal-radio-card-desc">{t('editGroupModal.optionalDesc')}</div>
                    </div>
                  </div>

                  {/* Obrigatorio */}
                  <div
                    className={`pdj-modal-radio-card${mandatory ? ' pdj-modal-radio-card--active' : ''}`}
                    onClick={() => handleMandatoryChange(true)}
                  >
                    <input
                      type="radio"
                      name="mandatory"
                      checked={mandatory}
                      onChange={() => handleMandatoryChange(true)}
                    />
                    <div>
                      <div className="pdj-modal-radio-card-label">{t('editGroupModal.required')}</div>
                      <div className="pdj-modal-radio-card-desc">{t('editGroupModal.requiredDesc')}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Precificacao */}
              <div className="pdj-modal-field">
                <label className="pdj-modal-label">
                  {t('editGroupModal.pricing')}
                  <span className="pdj-modal-required">*</span>
                </label>
                <select
                  className="pdj-modal-select"
                  id="precificacao"
                  name="precificacao"
                  value={precificacao}
                  onChange={(e) => setPrecificacao(e.target.value)}
                >
                  <option value="soma">{t('editGroupModal.pricingSum')}</option>
                  <option value="media">{t('editGroupModal.pricingAverage')}</option>
                  <option value="maior">{t('editGroupModal.pricingHighest')}</option>
                </select>

                <div className="pdj-modal-info-box">
                  {precificacao === "soma" && t('editGroupModal.pricingSumDesc')}
                  {precificacao === "media" && t('editGroupModal.pricingAverageDesc')}
                  {precificacao === "maior" && t('editGroupModal.pricingHighestDesc')}
                </div>
              </div>

            </div>

            <hr className="pdj-modal-divider" />

            {/* Seção: Itens vinculados */}
            <div className="pdj-modal-field">
              <label className="pdj-modal-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>
                  {type === 'pizza' ? t('editGroupModal.linkedPizzaItems') : t('editGroupModal.linkedItems')}
                </span>
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '400' }}>
                  {t('editGroupModal.itemsSelected', { count: selectedItemIds.size })}
                </span>
              </label>

              {/* Busca */}
              <input
                type="text"
                className="pdj-modal-input"
                placeholder={t('editGroupModal.searchItemsPlaceholder')}
                value={searchItem}
                onChange={(e) => setSearchItem(e.target.value)}
                style={{ marginBottom: '10px' }}
              />

              {/* Lista de itens */}
              <div style={{
                maxHeight: '250px',
                overflowY: 'auto',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                background: '#f8fafc',
              }}>
                {loadingItens ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>
                    {t('editGroupModal.loadingItems')}
                  </div>
                ) : filteredItens.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>
                    {searchItem ? t('editGroupModal.noItemsFound') : (type === 'pizza' ? t('editGroupModal.noPizzaItems') : t('editGroupModal.noItems'))}
                  </div>
                ) : (
                  Object.entries(itensPorCategoria).map(([categoria, itens]) => (
                    <div key={categoria}>
                      <div style={{
                        padding: '6px 12px',
                        background: '#e2e8f0',
                        fontSize: '11px',
                        fontWeight: '600',
                        color: '#475569',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                      }}>
                        {categoria}
                      </div>
                      {itens.map(item => (
                        <label
                          key={item._id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f1f5f9',
                            background: selectedItemIds.has(item._id) ? 'rgba(49, 140, 213, 0.05)' : 'transparent',
                            transition: 'background 0.1s',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedItemIds.has(item._id)}
                            onChange={() => toggleItem(item._id)}
                            style={{ width: '16px', height: '16px', accentColor: 'rgb(49, 140, 213)', cursor: 'pointer' }}
                          />
                          <span style={{ flex: 1, fontSize: '13px', color: '#1e293b' }}>
                            {item.title}
                          </span>
                          <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                            {formatPriceWithSymbol(item.price || 0)}
                          </span>
                        </label>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="pdj-modal-footer">
            <button type="button" className="pdj-modal-btn pdj-modal-btn--secondary" onClick={onClose}>
              {t('actions.cancel')}
            </button>
            <button type="submit" className="pdj-modal-btn pdj-modal-btn--primary" disabled={savingVinculos}>
              {savingVinculos ? t('editGroupModal.saving') : t('actions.save')}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default ModalEditGrupoAdicionais;
